import { useState, useRef } from 'react';
import { parseCdUploadFile } from './parseCdUpload';

const CD_FORMATS = [
  {
    id: 'expanded',
    label: 'Full Production Credits',
    hint: 'Adds producers, engineers, musicians, recording info, liner notes, awards, and product link (31 columns).',
  },
  {
    id: 'basic',
    label: 'CD Music Database',
    hint: 'Track, album, composer, artist, and publishing (13 columns).',
  },
];

const ALBUM_FIELD_LABELS = {
  title: 'Title',
  catalogue_num: 'Catalogue #',
  upc: 'UPC',
  title_artist: 'Title artist',
  release_date: 'Release date',
};

const PRODUCTION_FIELDS = [
  ['executive_producer', 'Executive producer'],
  ['producer', 'Producer'],
  ['associate_producer', 'Associate producer'],
  ['recording_engineer', 'Recording engineer'],
  ['editing_engineer', 'Editing engineer'],
  ['mixing_engineer', 'Mixing engineer'],
  ['mastering_engineer', 'Mastering engineer'],
  ['assistants', 'Assistants'],
  ['recording_dates', 'Recording dates'],
  ['recording_location', 'Recording location'],
  ['cover_art', 'Cover art'],
  ['cover_art_design', 'Cover art design'],
  ['package_design', 'Package design'],
  ['photography', 'Photography'],
  ['liner_notes_author', 'Liner notes author'],
  ['sponsor_credit', 'Sponsor credit'],
  ['awards', 'Awards'],
  ['link_to_product', 'Link to product'],
];

export function CdBulkUpload({ onSubmit }) {
  const [format, setFormat] = useState('expanded');
  const [parsedData, setParsedData] = useState(null);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const fileRef = useRef(null);

  const activeFormat = CD_FORMATS.find((f) => f.id === format);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseError(null);
    setParsedData(null);
    setSubmitResult(null);
    setFileName(file.name);
    setUploading(true);

    try {
      const data = await parseCdUploadFile(file, format);
      setParsedData(data);
      setExpanded(new Set());
    } catch (err) {
      setParseError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setParsedData(null);
    setParseError(null);
    setSubmitResult(null);
    setFileName('');
    setExpanded(new Set());
    if (fileRef.current) fileRef.current.value = '';
  };

  const toggleExpanded = (idx) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const updateAlbumField = (albumIdx, field, value) => {
    setParsedData((prev) => ({
      ...prev,
      albums: prev.albums.map((a, i) =>
        i === albumIdx ? { ...a, [field]: value || null } : a,
      ),
    }));
  };

  const updateTrackField = (albumIdx, trackIdx, field, value) => {
    setParsedData((prev) => ({
      ...prev,
      albums: prev.albums.map((a, i) => {
        if (i !== albumIdx) return a;
        return {
          ...a,
          tracks: a.tracks.map((t, j) =>
            j === trackIdx
              ? { ...t, [field]: normalizeTrackFieldValue(field, value) }
              : t,
          ),
        };
      }),
    }));
  };

  const deleteAlbum = (albumIdx) => {
    setParsedData((prev) => ({
      ...prev,
      albums: prev.albums.filter((_, i) => i !== albumIdx),
    }));
    setExpanded((prev) => {
      const next = new Set();
      prev.forEach((i) => {
        if (i < albumIdx) next.add(i);
        else if (i > albumIdx) next.add(i - 1);
      });
      return next;
    });
  };

  const deleteTrack = (albumIdx, trackIdx) => {
    setParsedData((prev) => ({
      ...prev,
      albums: prev.albums.map((a, i) => {
        if (i !== albumIdx) return a;
        return {
          ...a,
          tracks: a.tracks.filter((_, j) => j !== trackIdx),
        };
      }),
    }));
  };

  const totalTracks = parsedData
    ? parsedData.albums.reduce((n, a) => n + a.tracks.length, 0)
    : 0;

  const handleDryRun = async () => {
    if (!onSubmit || !parsedData) return;
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const result = await onSubmit({
        type: 'cd',
        data: {
          format: parsedData.format,
          albums: parsedData.albums.map(stripInternalFields),
          dryRun: true,
        },
      });
      setSubmitResult({ ok: true, result });
    } catch (err) {
      setSubmitResult({ ok: false, error: err.message || 'Dry run failed.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (!onSubmit || !parsedData) return;
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const result = await onSubmit({
        type: 'cd',
        data: {
          format: parsedData.format,
          albums: parsedData.albums.map(stripInternalFields),
          dryRun: false,
        },
      });
      setSubmitResult({ ok: true, result });
      setParsedData(null);
      setFileName('');
      setExpanded(new Set());
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      setSubmitResult({ ok: false, error: err.message || 'Submission failed.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="BulkUpload-typePane">
      <p className="AdminPortal-sectionLead">
        Upload CD / album metadata. Choose the spreadsheet format below.
      </p>

      <fieldset className="BulkUpload-formatGroup" disabled={!!parsedData}>
        <legend className="BulkUpload-formatLegend">Spreadsheet format</legend>
        {CD_FORMATS.map((f) => (
          <label
            key={f.id}
            className={`BulkUpload-formatOption ${format === f.id ? 'BulkUpload-formatOption--active' : ''}`}
          >
            <input
              type="radio"
              name="cd-format"
              value={f.id}
              checked={format === f.id}
              onChange={() => setFormat(f.id)}
            />
            <span className="BulkUpload-formatBody">
              <span className="BulkUpload-formatLabel">{f.label}</span>
              <span className="BulkUpload-formatHint">{f.hint}</span>
            </span>
          </label>
        ))}
      </fieldset>

      {parseError && (
        <div className="AdminPortal-banner AdminPortal-banner--error" role="alert">
          {parseError}
        </div>
      )}
      {submitResult && submitResult.ok && (
        <SubmitSummary summary={submitResult.result} />
      )}
      {submitResult && !submitResult.ok && (
        <div className="AdminPortal-banner AdminPortal-banner--error" role="alert">
          {submitResult.error}
        </div>
      )}

      {!parsedData && (
        <div className="BulkUpload-dropzone">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className="BulkUpload-fileInput"
            id="bulk-upload-input-cd"
          />
          <label htmlFor="bulk-upload-input-cd" className="BulkUpload-dropLabel">
            {uploading ? (
              <span>Parsing {fileName}...</span>
            ) : (
              <>
                <span className="BulkUpload-dropIcon">+</span>
                <span>Click to select a .xls or .xlsx file</span>
                <span className="BulkUpload-dropHint">
                  Format: <strong>{activeFormat.label}</strong> · {activeFormat.hint}
                </span>
              </>
            )}
          </label>
        </div>
      )}

      {parsedData && (
        <>
          <div className="BulkUpload-fileBadge">
            <span>{fileName}</span>
            <button type="button" className="BulkUpload-clearBtn" onClick={handleReset}>
              Clear
            </button>
          </div>

          <div className="BulkUpload-summary">
            <span className="BulkUpload-summaryChip">
              {parsedData.albums.length} album{parsedData.albums.length !== 1 ? 's' : ''}
            </span>
            <span className="BulkUpload-summaryChip">
              {totalTracks} track{totalTracks !== 1 ? 's' : ''}
            </span>
            <span className="BulkUpload-summaryChip">
              Format: {activeFormat.label}
            </span>
          </div>

          <div className="BulkUpload-sheetSection">
            {parsedData.albums.map((album, i) => (
              <AlbumCard
                key={i}
                album={album}
                format={parsedData.format}
                isExpanded={expanded.has(i)}
                onToggle={() => toggleExpanded(i)}
                onUpdateAlbum={(field, value) => updateAlbumField(i, field, value)}
                onUpdateTrack={(ti, field, value) => updateTrackField(i, ti, field, value)}
                onDeleteAlbum={() => deleteAlbum(i)}
                onDeleteTrack={(ti) => deleteTrack(i, ti)}
              />
            ))}
          </div>

          <div className="BulkUpload-actions">
            <button type="button" className="ChatButton" onClick={handleReset}>
              Cancel
            </button>
            <button
              type="button"
              className="ChatButton"
              onClick={handleDryRun}
              disabled={submitting || parsedData.albums.length === 0}
              title="Run the same DB matching logic as a real upload, then roll back — no rows saved, no embeddings"
            >
              {submitting ? 'Running…' : 'Preview dry run'}
            </button>
            <button
              type="button"
              className="ChatButton AdminPortal-submitBtn BulkUpload-confirmBtn"
              onClick={handleConfirm}
              disabled={submitting || parsedData.albums.length === 0}
            >
              {submitting
                ? 'Submitting...'
                : `Confirm & submit (${parsedData.albums.length} album${parsedData.albums.length !== 1 ? 's' : ''})`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function AlbumCard({
  album,
  format,
  isExpanded,
  onToggle,
  onUpdateAlbum,
  onUpdateTrack,
  onDeleteAlbum,
  onDeleteTrack,
}) {
  return (
    <div className={`BulkUpload-albumCard ${isExpanded ? 'BulkUpload-albumCard--expanded' : ''}`}>
      <div className="BulkUpload-albumHeader">
        <button
          type="button"
          className="BulkUpload-albumToggle"
          onClick={onToggle}
          aria-expanded={isExpanded}
        >
          <span className="BulkUpload-albumChevron">{isExpanded ? '▾' : '▸'}</span>
          <span className="BulkUpload-albumHeaderText">
            <span className="BulkUpload-albumTitle">{album.title || '(untitled)'}</span>
            <span className="BulkUpload-albumMeta">
              {[album.catalogue_num, album.title_artist, album.release_date]
                .filter(Boolean)
                .join(' · ')}
              {' · '}
              {album.tracks.length} track{album.tracks.length !== 1 ? 's' : ''}
            </span>
          </span>
        </button>
        <button
          type="button"
          className="ChatButton AdminPortal-delete"
          onClick={onDeleteAlbum}
        >
          Delete
        </button>
      </div>

      {isExpanded && (
        <div className="BulkUpload-albumBody">
          <div className="BulkUpload-albumFields">
            {Object.entries(ALBUM_FIELD_LABELS).map(([field, label]) => (
              <AlbumFieldRow
                key={field}
                label={label}
                value={album[field]}
                onChange={(v) => onUpdateAlbum(field, v)}
              />
            ))}
          </div>

          {format === 'expanded' && (
            <>
              <div className="BulkUpload-albumSubheading">Production credits</div>
              <div className="BulkUpload-albumFields">
                {PRODUCTION_FIELDS.map(([field, label]) => (
                  <AlbumFieldRow
                    key={field}
                    label={label}
                    value={album[field]}
                    onChange={(v) => onUpdateAlbum(field, v)}
                  />
                ))}
              </div>

              {album.liner_notes_text && (
                <>
                  <div className="BulkUpload-albumSubheading">Liner notes</div>
                  <textarea
                    className="AdminPortal-textarea BulkUpload-linerNotes"
                    value={album.liner_notes_text || ''}
                    onChange={(e) => onUpdateAlbum('liner_notes_text', e.target.value)}
                    rows={6}
                  />
                </>
              )}
            </>
          )}

          <div className="BulkUpload-albumSubheading">
            Tracks ({album.tracks.length})
          </div>
          <div className="AdminPortal-tableWrap">
            <table className="AdminPortal-table BulkUpload-trackTable">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Title</th>
                  <th scope="col">Composer</th>
                  <th scope="col">Time</th>
                  <th scope="col">V/I</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {album.tracks.map((t, ti) => (
                  <tr key={ti}>
                    <td>
                      <InlineTextInput
                        value={trackNumberDisplay(t.track_number)}
                        onChange={(v) => onUpdateTrack(ti, 'track_number', v)}
                        size={4}
                      />
                    </td>
                    <td>
                      <InlineTextInput
                        value={t.song_title || ''}
                        onChange={(v) => onUpdateTrack(ti, 'song_title', v)}
                      />
                    </td>
                    <td>
                      <InlineTextInput
                        value={t.composer || ''}
                        onChange={(v) => onUpdateTrack(ti, 'composer', v)}
                      />
                    </td>
                    <td>
                      <InlineTextInput
                        value={t.duration || ''}
                        onChange={(v) => onUpdateTrack(ti, 'duration', v)}
                        size={6}
                      />
                    </td>
                    <td>
                      <InlineTextInput
                        value={t.vocal_instrumental || ''}
                        onChange={(v) => onUpdateTrack(ti, 'vocal_instrumental', v)}
                        size={2}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="ChatButton AdminPortal-delete"
                        onClick={() => onDeleteTrack(ti)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function AlbumFieldRow({ label, value, onChange }) {
  return (
    <label className="BulkUpload-albumFieldRow">
      <span className="BulkUpload-albumFieldLabel">{label}</span>
      <input
        type="text"
        className="AdminPortal-input"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function InlineTextInput({ value, onChange, size }) {
  return (
    <input
      type="text"
      className="AdminPortal-input BulkUpload-editInput"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      size={size}
    />
  );
}

function pluralize(n) {
  return Number(n) === 1 ? '' : 's';
}

function trackNumberDisplay(n) {
  if (n === null || n === undefined) return '';
  if (n >= 100) {
    const disc = Math.floor(n / 100);
    const track = n % 100;
    return `Disc ${disc} track ${track}`;
  }
  return String(n);
}

function SubmitSummary({ summary }) {
  if (!summary) return null;
  const s = summary.summary || summary;
  const isDryRun = Boolean(s.dryRun);

  const lines = [];
  lines.push(`${s.newAlbums ?? 0} new album${pluralize(s.newAlbums)} would be added`);
  if ((s.matchedAlbums ?? 0) > 0) {
    const enriched = s.enrichedAlbums ?? 0;
    const tail = enriched > 0
      ? ` (${enriched} would be enriched with new production details)`
      : '';
    lines.push(
      `${s.matchedAlbums} album${pluralize(s.matchedAlbums)} already in the database${tail}`,
    );
  }
  const trackParts = [
    `${s.newAlbumTracks ?? 0} new track${pluralize(s.newAlbumTracks)} would be linked`,
  ];
  if ((s.enrichedAlbumTracks ?? 0) > 0) {
    trackParts.push(
      `${s.enrichedAlbumTracks} existing track${pluralize(s.enrichedAlbumTracks)} would be enriched`,
    );
  }
  if ((s.skippedDuplicateTracks ?? 0) > 0) {
    trackParts.push(`${s.skippedDuplicateTracks} already present (no change)`);
  }
  lines.push(trackParts.join(' · '));
  lines.push(
    `${s.newSongs ?? 0} new song${pluralize(s.newSongs)} would be created · ${s.matchedSongs ?? 0} matched existing`,
  );
  if (isDryRun) {
    lines.push('Embeddings: not run in dry run (would run after a real submit).');
  } else {
    const embedParts = [
      `${s.embeddedAlbums ?? 0} new album${pluralize(s.embeddedAlbums)}`,
    ];
    if ((s.reembeddedAlbums ?? 0) > 0) {
      embedParts.push(
        `${s.reembeddedAlbums} re-embedded album${pluralize(s.reembeddedAlbums)}`,
      );
    }
    embedParts.push(`${s.embeddedSongs ?? 0} song${pluralize(s.embeddedSongs)}`);
    if ((s.reembeddedSongs ?? 0) > 0) {
      embedParts.push(
        `${s.reembeddedSongs} re-embedded song${pluralize(s.reembeddedSongs)}`,
      );
    }
    lines.push(`Embeddings generated: ${embedParts.join(', ')}`);
  }

  return (
    <div
      className={`AdminPortal-banner ${isDryRun ? 'AdminPortal-banner--info' : 'AdminPortal-banner--success'}`}
      role="status"
    >
      <strong>
        {isDryRun
          ? 'Dry run complete — nothing was saved.'
          : 'Upload complete.'}
      </strong>
      <ul className="BulkUpload-summaryList">
        {lines.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
      {!isDryRun && (s.embeddingFailures ?? 0) > 0 && (
        <div className="BulkUpload-summaryNote">
          {s.embeddingFailures} embedding{pluralize(s.embeddingFailures)} failed —
          the record{pluralize(s.embeddingFailures)} {s.embeddingFailures === 1 ? 'was' : 'were'} still
          inserted and will be re-embedded on the next upload.
        </div>
      )}
      {isDryRun && s.previewNewSongs != null && s.previewNewTrackLinks != null && (
        <div className="BulkUpload-dryRunDetails">
          <div className="BulkUpload-dryRunBlock">
            <div className="BulkUpload-dryRunHeading">New songs</div>
            {s.previewNewSongs.length === 0 ? (
              <p className="BulkUpload-dryRunEmpty">None</p>
            ) : (
              <ul className="BulkUpload-dryRunList">
                {s.previewNewSongs.map((row, i) => (
                  <li key={`s-${i}-${row.title}`}>
                    <span className="BulkUpload-dryRunTitle">{row.title}</span>
                    {row.composer ? (
                      <span className="BulkUpload-dryRunMeta"> — {row.composer}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="BulkUpload-dryRunBlock">
            <div className="BulkUpload-dryRunHeading">New track links</div>
            {s.previewNewTrackLinks.length === 0 ? (
              <p className="BulkUpload-dryRunEmpty">None</p>
            ) : (
              <ul className="BulkUpload-dryRunList">
                {s.previewNewTrackLinks.map((row, i) => (
                  <li key={`t-${i}-${row.albumTitle}-${row.songTitle}-${row.trackNumber}`}>
                    <span className="BulkUpload-dryRunAlbum">{row.albumTitle}</span>
                    <span className="BulkUpload-dryRunMeta">
                      {' '}
                      ·{' '}
                      {row.trackNumber === null ||
                      row.trackNumber === undefined ||
                      row.trackNumber === ''
                        ? '—'
                        : trackNumberDisplay(row.trackNumber)}
                      {' '}
                      ·{' '}
                    </span>
                    <span className="BulkUpload-dryRunTitle">{row.songTitle}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function normalizeTrackFieldValue(field, value) {
  if (value === undefined || value === null) return null;
  const str = String(value).trim();
  if (str === '') return null;
  if (field === 'track_number') {
    const disc = str.match(/^Disc\s+(\d+)\s+track\s+(\d+)$/i);
    if (disc) return Number(disc[1]) * 100 + Number(disc[2]);
    const n = Number(str);
    return Number.isFinite(n) ? Math.trunc(n) : str;
  }
  return str;
}

function stripInternalFields(album) {
  const { _idx, tracks, ...rest } = album;
  return {
    ...rest,
    tracks: tracks.map(({ _idx: _ti, ...t }) => t),
  };
}
